import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxRootState } from "../../shared/redux/store";
import { ReduxSliceState } from "../../shared/types";
import { withPayloadType } from "../../shared/utils";
import { KitOrderConfirmation, ResolvedPaymentSource } from "../../api/types";
import { CreateKitOrderProps, ReduxKitOrdersState } from "../types";

const initialState: ReduxKitOrdersState = {
    state: 'READY',
    billingState: 'READY',
    quantity: undefined,
    orgStripeBilling: undefined,
    confirmation: undefined,
    errorMessage: undefined,
};

export const loadOrgStripeBilling = createAction('kitOrders/loadOrgStripeBilling', withPayloadType<number | undefined>());
export const createKitOrder = createAction('kitOrders/createKitOrder', withPayloadType<CreateKitOrderProps>());
export const resetKitOrder = createAction('kitOrders/resetKitOrder');

const kitOrdersSlice = createSlice({
    name: 'kitOrders',
    initialState,
    reducers: {
        setState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.state = action.payload;
        },
        setBillingState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.billingState = action.payload;
        },
        setQuantity: (state, action: PayloadAction<number>) => {
            state.quantity = action.payload;
        },
        setOrgStripeBilling: (state, action: PayloadAction<ResolvedPaymentSource | undefined>) => {
            state.orgStripeBilling = action.payload;
        },
        setConfirmation: (state, action: PayloadAction<KitOrderConfirmation | undefined>) => {
            state.confirmation = action.payload;
        },
        setErrorMessage: (state, action: PayloadAction<string | undefined>) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        // resetKitOrder is dispatched directly by the ShipToClinic page (on mount, and from
        // "Place Another Order") rather than routed through a saga - handle it here so it
        // actually clears stale confirmation state instead of being a no-op.
        builder.addCase(resetKitOrder, (state) => {
            state.state = 'READY';
            state.billingState = 'READY';
            state.quantity = undefined;
            state.orgStripeBilling = undefined;
            state.confirmation = undefined;
            state.errorMessage = undefined;
        });
    },
});

export default kitOrdersSlice;

const selectSelf = (state: ReduxRootState) => state.kitOrders;
export const selectKitOrdersIsBusy = createSelector(selectSelf, self => self.state === 'BUSY');
export const selectKitOrdersIsSuccess = createSelector(selectSelf, self => self.state === 'SUCCESS');
export const selectKitOrdersIsError = createSelector(selectSelf, self => self.state === 'ERROR');
export const selectKitOrdersQuantity = createSelector(selectSelf, self => self.quantity);
export const selectKitOrdersBillingIsBusy = createSelector(selectSelf, self => self.billingState === 'BUSY');
export const selectKitOrdersOrgStripeBilling = createSelector(selectSelf, self => self.orgStripeBilling);
export const selectKitOrdersConfirmation = createSelector(selectSelf, self => self.confirmation);
export const selectKitOrdersErrorMessage = createSelector(selectSelf, self => self.errorMessage);
